import { arrayBufferToBlob, PhotoDB } from '@/accessors/indexedDB/disasterEmergencyPhotoIdb'
import { OPERATION } from '@/constants/accessor/indexedDB/disasterEmergencyPhotoDB'
import type {
  Directory,
  FileObject,
  InsertPhotoData,
  PhotoState
} from '@/types/accessor/indexedDB/disasterEmergencyPhotoDB'
import { deleteImageFromS3, uploadImageToS3 } from '../s3/disasterEmergencyPhotoS3Accessor'
import { S3_DIRECTORY_NAMES } from '@/constants/accessor/s3/directoryName'
import { useNetworkStore } from '@/stores/networkStore'

const photoDB = PhotoDB.instance
/**
 * indexedDBに画像データを挿入する
 * @param photoData 画像データ
 * @param option 画像オプション
 * @returns 画像ファイルパスリスト
 */
const insertPhoto = async (
  photoData: FileObject,
  option: Directory
): Promise<[InsertPhotoData, Blob | null]> => {
  const directoryData = {
    govCode: option.govCode,
    disasterId: option.disasterId,
    assessmentId: option.assessmentId,
    field: option.field
  }
  const fileNameData = {
    timestamp: Date.now(),
    extension: '.' + photoData.image.type.split('/')[1]
  }
  const fileName = photoDB.generateNewFileName(directoryData, fileNameData)
  const newPhoto: PhotoState = {
    isOnS3: false,
    s3Operation: OPERATION.INSERT,
    // arrayBuffer: await photoData.image.arrayBuffer(),
    // NOTE:
    // insertItem() 内で compressor による圧縮 → compressedBlob.arrayBuffer() を生成して保存するため、
    // ここで元ファイルの arrayBuffer を作ると “二重に巨大バッファ” を持つことになり、iOS Safari のクラッシュ要因になる。
    // arrayBuffer は insertItem 側に任せる。
    isStoredByUser: false,
    mime: photoData.mime,
    fileName: fileName.directory + fileName.fileName,
    extension: '.' + photoData.image.type.split('/')[1],
    expirationDate: fileNameData.timestamp
  }
  const compressedBlob = await photoDB.insertItem(newPhoto, photoData.image)
  const insertStoreData = {
    imageFilePathList: [fileName.fileName + newPhoto.extension]
  }

  return [insertStoreData, compressedBlob]
}

/**
 * indexedDBに指定されたPhotoStateをそのまま挿入（更新）する
 * @param photoData 画像データ
 * @returns なし
 */
const upsertPhoto = async (photoData: PhotoState): Promise<void> => {
  await photoDB.upsertItem(photoData)
}

/**
 * IndexedDB内をkeyで検索し、一致するレコードの削除フラグを立てる
 * @param key IndexedDBのkey
 * @returns なし
 */
const updateStateToDelete = async (key: string): Promise<void> => {
  await photoDB.updateStateToDelete(key)
}

/**
 * indexedDBにある画像を調査項目単位でS3に保存する
 * @param directoryData S3ディレクトリパスに使用する文字列の構造体
 * @returns なし
 */
const uploadImageToS3Batch = async (directoryData: Directory): Promise<void> => {
  const prefix = photoDB.generateDirectory(directoryData)
  const networkStore = useNetworkStore()
  let files: PhotoState[] | undefined
  try {
    files = await photoDB.queryPrefixMatch(prefix)
    if (networkStore.getIsOnline) {
      // オンライン時: S3に保存後indexedDBに保存
      await Promise.all(
        files
          .filter(photo => photo.s3Operation === OPERATION.INSERT)
          .map(async photo => {
            let blob: Blob | null = null
            let file: File | null = null
            try {
              blob = arrayBufferToBlob(photo.mime, photo.arrayBuffer)
              file = new File([blob], photo.fileName, { type: photo.mime })
              return await uploadImageToS3(
                prefix,
                photo.fileName.replace(prefix, '') + photo.extension,
                file
              ).then(async () => {
                photo.isStoredByUser = true
                photo.isOnS3 = true
                photo.s3Operation = OPERATION.STAY
                await photoDB.insertItem(photo)
              })
            } finally {
              blob = null
              file = null
            }
          })
      )
      await Promise.all(
        files
          .filter(photo => photo.s3Operation === OPERATION.DELETE)
          .map(async photo => {
            return await deleteImageFromS3(prefix, photo.fileName + photo.extension).then(
              async () => {
                await photoDB.deleteItem(photo.fileName)
              }
            )
          })
      )
    } else {
      // オフライン時: indexedDBにのみ保存
      await Promise.all(
        files
          .filter(photo => photo.s3Operation === OPERATION.INSERT)
          .map(async photo => {
            photo.isStoredByUser = true
            photo.isOnS3 = false
            photo.s3Operation = OPERATION.INSERT
            await photoDB.insertItem(photo)
          })
      )
    }
  } finally {
    files = undefined
  }
}

/**
 * 特定の調査の画像を一括でアップロードする
 * @param fieldNames 調査項目リスト
 * @param govCode 自治体ID
 * @param disasterId 災害ID
 * @param assessmentId 判定ID
 */
const uploadInspectionPhoto = async (
  fieldNames: string[],
  govCode: string,
  disasterId: string,
  assessmentId: string
): Promise<void> => {
  // アップロードできなかった写真は無視する
  let errCnt = 0
  await Promise.allSettled(
    fieldNames.map(async field => {
      await uploadImageToS3Batch({
        govCode: govCode,
        disasterId: disasterId,
        assessmentId: assessmentId,
        field: field
      }).catch(async e => {
        console.error(`${field} upload failed`)
        throw e
      })
    })
  ).then(results => {
    for (const r of results) {
      if (r.status === 'rejected') {
        console.error(r.reason)
        errCnt++
      }
    }
    if (errCnt > 0) {
      throw new Error('画像のアップロードに失敗した調査項目が存在します。')
    }
  })
}

/**
 * S3に1つの調査データに紐づけされた調査画像を一括でアップロードする
 * @param reqData 調査実施結果API(update)リクエストデータ群
 */
const executeS3Upload = async (
  govCode: string,
  disasterId: string,
  assessmentId: string
): Promise<void> => {
  try {
    const fieldNames = [...Object.values(S3_DIRECTORY_NAMES)]

    await uploadInspectionPhoto(fieldNames, govCode, disasterId, assessmentId)
  } catch (e) {
    console.error(`assessmentId:${assessmentId}に添付された画像のアップロードに失敗しました。`)
    throw e
  }
}

/**
 * indexedDBに保存されている画像リストを取得する
 * @param itemDirectory
 * @param fileNameList
 * @returns 画像データリスト
 * @note 有効な画像（削除フラグが立っていないもの）のみ返却する
 */
const getItems = async (
  itemDirectory: string,
  fileNameList: string[]
): Promise<(PhotoState | undefined)[]> => {
  return await Promise.all(
    fileNameList.map(async e => {
      const key = (itemDirectory + e).replace(/\.[^.]+$/, '')
      const item = await photoDB.getItem(key)
      // 有効な画像（削除フラグが立っていないもの）のみ返却する
      if (item?.s3Operation !== OPERATION.DELETE) return item
    })
  )
}

/**
 * 検索文字列（前方一致）に基づく画像データをindexedDBから取得する
 * @param prefix 検索文字列（前方一致）
 * @returns 画像データ
 */
const queryPrefixMatchItems = async (prefix: string): Promise<PhotoState[]> => {
  const keys = await photoDB.queryPrefixMatchKeys(prefix)
  const items = await Promise.all(
    keys.map(async key => {
      const item = await photoDB.getItemByPrimaryKey(key as string)
      if (item?.s3Operation !== OPERATION.DELETE) return item
      return undefined
    })
  )
  return items.filter((item): item is PhotoState => item !== undefined)
}

export default {
  insertPhoto,
  upsertPhoto,
  updateStateToDelete,
  executeS3Upload,
  getItems,
  queryPrefixMatchItems
}
